import { FunctionComponent, useEffect, useState } from "react"
import { Alert, Button, Col, Modal, Row } from "react-bootstrap"
import Container from "react-bootstrap/Container"
import { useNavigate, useParams } from "react-router-dom"
import PassForm from "../components/PassForm"
import { Pass, PassType } from "../data/models.interface"
import { useCreatePassQuery } from "../hooks/queries/useCreatePassQuery"
import { useDeletePassQuery } from "../hooks/queries/useDeletePassQuery"
import { useGetDoorsQuery } from "../hooks/queries/useGetDoorsQuery"
import { useGetPassesQuery } from "../hooks/queries/useGetPassesQuery"
import { useGetRentManagerPropertiesQuery } from "../hooks/queries/useGetRentManagerPropertiesQuery"
import { useGetRentManagerUnitsQuery } from "../hooks/queries/useGetRentManagerUnitsQuery"
import { useUpdatePassQuery } from "../hooks/queries/useUpdatePassQuery"

interface IPassPage {}
const PassPage: FunctionComponent<IPassPage> = () => {
	const navigate = useNavigate()
	const params = useParams()
	const passId = params.passId
	//
	const passesQuery = useGetPassesQuery()
	const passLoaded = passesQuery.data?.passes?.find((s) => s.id === passId)
	const createPassQuery = useCreatePassQuery()
	const updatePassQuery = useUpdatePassQuery(passId || "error")
	const deletePassQuery = useDeletePassQuery(passId || "error")
	const doorsQuery = useGetDoorsQuery()
	const doors = doorsQuery.data?.doors
	const propertiesQuery = useGetRentManagerPropertiesQuery()
	const properties = propertiesQuery.data?.rentManagerProperties
	const unitsQuery = useGetRentManagerUnitsQuery()
	const units = unitsQuery.data?.rentManagerUnits
	//
	const [pass, setPass] = useState<Pass>()
	const [touched, setTouched] = useState(new Map<string, boolean>())
	const validationErrors = validate(pass)
	//
	useEffect(() => {
		// only init state once
		if (pass == null) {
			if (passId === "new") setPass({ type: PassType.ROLE } as Pass)
			else if (passLoaded) setPass(passLoaded)
		}
	}, [passId, passLoaded, pass])
	// error message
	const invalidIdError =
		passId !== "new" && passesQuery.data != null && passLoaded == null ? "No Pass found" : undefined
	const error = createPassQuery.error || updatePassQuery.error || passesQuery.error || invalidIdError
	// loading layout
	const loading = !pass || !doors || !properties || !units
	if (loading) return <LoadingLayout error={error} />
	// normal layout
	const goBack = () => navigate(-1)
	const save = async () => {
		// show errors on all fields
		const touchMap = new Map<string, boolean>()
		touchMap.set("name", true)
		touchMap.set("type", true)
		touchMap.set("propertyId", true)
		touchMap.set("unitId", true)
		setTouched(touchMap)
		//
		if (!(pass.type === PassType.AMENITIES || pass.type === PassType.EXTERIOR || pass.type === PassType.UNIT)) {
			pass.propertyId = undefined
			pass.propertyName = undefined
			pass.propertyShortName = undefined
		}
		if (!(pass.type === PassType.UNIT)) {
			pass.unitId = undefined
			pass.unitName = undefined
		}
		//
		try {
			const errorCount = Array.from(validationErrors).filter((e) => e?.length > 0).length
			if (errorCount === 0) {
				if (passId === "new") await createPassQuery.query({ body: pass })
				else await updatePassQuery.query({ body: pass })
				navigate(-1)
			}
		} catch (e) {
			return
		}
	}
	//
	return (
		<Container>
			<h2 className="text-center mt-4">Pass</h2>
			{error && <Alert variant={"warning"}>{error}</Alert>}
			<PassForm
				pass={pass}
				setPass={setPass}
				touched={touched}
				setTouched={setTouched}
				validationErrors={validationErrors}
				doors={doors}
				properties={properties}
				units={units}
			/>
			<Row>
				<Col xs="auto">
					<Button variant="secondary" className="mb-4" onClick={goBack}>
						Back
					</Button>
				</Col>
				{/* Hide delete for new passes */}
				{passId !== "new" && (
					<Col xs="auto">
						<DeleteButton query={deletePassQuery.query} loading={deletePassQuery.loading} />
					</Col>
				)}
				<Col></Col>
				<Col xs="auto">
					<Button variant="primary" className="mb-4" disabled={loading} onClick={save}>
						Save
					</Button>
				</Col>
			</Row>
		</Container>
	)
}
export default PassPage

const LoadingLayout = ({ error }: { error?: string }) => {
	return (
		<Container style={{ maxWidth: 500 }}>
			<h2 className="text-center mt-4">Pass</h2>
			{error && <Alert variant={"warning"}>{error}</Alert>}
			<div className="text-center">Loading</div>
		</Container>
	)
}

const validate = (pass?: Pass) => {
	const validationErrors = new Map<string, string>()
	if (!pass?.name) validationErrors.set("name", "Name is required.")
	if (!pass?.type) validationErrors.set("type", "Type is required.")
	if (pass?.type === PassType.AMENITIES || pass?.type === PassType.EXTERIOR || pass?.type === PassType.UNIT) {
		if (!pass?.propertyId) validationErrors.set("propertyId", "Property is required.")
	}
	if (pass?.type === PassType.UNIT) {
		if (!pass?.unitId) validationErrors.set("unitId", "Unit is required.")
	}
	return validationErrors
}

interface IDeleteButton {
	query: () => Promise<void>
	loading: boolean
}
const DeleteButton = ({ query, loading }: IDeleteButton) => {
	const [show, setShow] = useState(false)
	const navigate = useNavigate()

	const onConfirm = async () => {
		try {
			await query()
			setShow(false)
			navigate(-1)
		} catch (e) {
			// swallow; hook exposes error for display in page
		}
	}

	return (
		<>
			<Button variant="danger" className="mb-4" disabled={loading} onClick={() => setShow(true)}>
				Delete
			</Button>

			<Modal show={show} onHide={() => setShow(false)} centered>
				<Modal.Header closeButton>
					<Modal.Title>Confirm Delete</Modal.Title>
				</Modal.Header>
				<Modal.Body>Are you sure you want to delete this pass? This action cannot be undone.</Modal.Body>
				<Modal.Footer>
					<Button variant="secondary" onClick={() => setShow(false)}>
						Cancel
					</Button>
					<Button variant="danger" onClick={onConfirm} disabled={loading}>
						Delete
					</Button>
				</Modal.Footer>
			</Modal>
		</>
	)
}
